import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { Filter, HandoverProtocol, TableComponentProps, TableData, TableInnerProps } from '../models/table-models';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import {
    getHandoversExcelByFilter,
    getHandoversPDFByFilter,
    getHandoversSummary,
    useGetAllShortPassports,
    useGetHandoverProtocols as fetchHandoverProtocols,
} from '../utils/warehouses-hooks';
import { Button, Form, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { CustomTable } from '../components/CustomTable';
import { handoverProtocolsShortTableHeaders } from '../utils/Enums/TableHeaders';
import { NoDataComponent } from '../components/NoDataComponent';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import dateFormat from 'dateformat';
import { ErrorComponent } from '../components/ErrorComponent';
import ErrorBoundary from '../components/ErrorBoundary';
import {
    useDeleteProtocol,
    useGetBranchByIdInRequestView as getBranchByIdInRequestView,
    useGetClient as getClientById,
} from '../utils/clients-hooks';
import { AddEditProtocol } from './AddEditProtocol';
import { HandoversSummary } from '../models/warehouse-models';
import { TableRequestParams } from '../utils/data-hooks';
import { PaginationComponent } from '../components/Pagination';
import { NewRequestProvider, NewRequestContext } from '../context/NewRequestContext';
import { NewRequest } from './NewRequest';
import { Role } from '../utils/Enums/Roles';

const protocolTypes = ['INSTALLATION', 'MAINTENANCE', 'DEVICE_RETURN', 'TEST', 'RENAMED_BRANCH_REPLACEMENT'];
const protocolSortFields = ['date', 'clientName', 'branchName', 'address', 'taskCategory', 'distributor'];
const protocolSortDirections = ['ASC', 'DESC'];

export const ProtocolTypeFilter = ({
    filter,
    setFilter,
    pagination,
}: Pick<TableComponentProps, 'filter' | 'setFilter' | 'pagination'>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });

    const updateProtocolTypeFilter = (taskCategory: string) => {
        pagination.setCurrentPage(1);
        setFilter((prev) => ({
            ...prev,
            type: taskCategory || undefined,
        }));
    };
    const updateProtocolSortFilter = (field: 'sortBy' | 'sortDirection', value: string) => {
        pagination.setCurrentPage(1);
        setFilter((prev) => ({
            ...prev,
            [field]: value || undefined,
        }));
    };
    const getSortFieldLabel = (sortField: string) => {
        if (sortField === 'clientName') {
            return t('client');
        }
        if (sortField === 'taskCategory') {
            return t('taskType');
        }

        return tableT(sortField);
    };

    return (
        <div className='d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 protocol-type-filter'>
            <div className='d-flex align-items-center text-nowrap protocol-filter-field'>
                <Form.Label className='mb-0 me-2'>{t('taskType')}:</Form.Label>
                <Form.Select
                    className='w-auto'
                    value={filter.type ?? ''}
                    onChange={(event) => updateProtocolTypeFilter(event.target.value)}
                >
                    <option value=''>{t('all')}</option>
                    {protocolTypes.map((type) => (
                        <option key={type} value={type}>
                            {t(type)}
                        </option>
                    ))}
                </Form.Select>
            </div>
            <div className='d-flex align-items-center text-nowrap protocol-filter-field'>
                <Form.Label className='mb-0 me-2'>{t('sortBy')}:</Form.Label>
                <Form.Select
                    className='w-auto'
                    value={filter.sortBy ?? 'date'}
                    onChange={(event) => updateProtocolSortFilter('sortBy', event.target.value)}
                >
                    {protocolSortFields.map((sortField) => (
                        <option key={sortField} value={sortField}>
                            {getSortFieldLabel(sortField)}
                        </option>
                    ))}
                </Form.Select>
            </div>
            <div className='d-flex align-items-center text-nowrap protocol-filter-field'>
                <Form.Label className='mb-0 me-2'>{t('sortDirection')}:</Form.Label>
                <Form.Select
                    className='w-auto'
                    value={filter.sortDirection ?? 'DESC'}
                    onChange={(event) => updateProtocolSortFilter('sortDirection', event.target.value)}
                >
                    {protocolSortDirections.map((direction) => (
                        <option key={direction} value={direction}>
                            {direction === 'ASC' ? t('ascending') : t('descending')}
                        </option>
                    ))}
                </Form.Select>
            </div>
        </div>
    );
};

interface ProtocolsRequestParams extends TableRequestParams {
    hasSearched?: boolean;
    searchVersion?: number;
}

const getEmptyProtocolsData = (params: ProtocolsRequestParams): TableData<HandoverProtocol[]> => ({
    items: [],
    pager: {
        itemsCount: 0,
        itemsPerPage: params['pager.itemsPerPage'] ?? 0,
        page: params['pager.page'] ?? 1,
        pagesCount: 0,
        totalCount: 0,
    },
});

const fetchProtocolsWhenReady = async (params: ProtocolsRequestParams): Promise<TableData<HandoverProtocol[]>> => {
    if (!params.hasSearched) {
        return getEmptyProtocolsData(params);
    }

    return fetchHandoverProtocols(params);
};

export const Protocols = ({
    filter,
    pagination,
    appliedFilter,
    hasSearched = true,
    searchVersion = 0,
}: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const currentDataFilter = appliedFilter ?? filter;
    const [getProtocols, fetchNewProtocols] = useAsyncResource(fetchProtocolsWhenReady, {
        'pager.page': pagination.currentPage,
        'pager.itemsPerPage': pagination.pageSize,
        filter: currentDataFilter,
        hasSearched,
        searchVersion,
    });

    const refetchProtocolsHook = async () => {
        if (!hasSearched) {
            return;
        }

        resourceCache(fetchHandoverProtocols).clear();
        fetchNewProtocols({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            filter: currentDataFilter,
            hasSearched,
            searchVersion,
        });
    };

    return (
        <>
            <ErrorBoundary fallback={<ErrorComponent errorText={t('handoverProtocols')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <ProtocolsInner
                        appliedFilter={currentDataFilter}
                        hasSearched={hasSearched}
                        pagination={pagination}
                        getEntities={getProtocols}
                        refetchEntitiesHook={refetchProtocolsHook}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
};

const ProtocolsInner = ({
    getEntities,
    pagination,
    refetchEntitiesHook,
    appliedFilter,
    hasSearched,
}: TableInnerProps<HandoverProtocol> & {
    appliedFilter: Filter;
    hasSearched: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });

    const { items, pager } = getEntities();
    const [getPassports] = useAsyncResource(useGetAllShortPassports, false);
    const serviceableMaterialPassportIds = new Set([
        ...getPassports()
            .filter((passport) => passport.type === 'OTHER' && passport.serviceable)
            .map((passport) => passport.id)
            .filter((passportId): passportId is number => passportId != null),
        ...items.flatMap((protocol) =>
            protocol.items
                .filter((item) => item.itemType === 'OTHER' && item.oilId != null)
                .map((item) => item.itemId)
        ),
    ]);
    const [comment, setComment] = useState('');

    const useDelete = async (protocol: HandoverProtocol) => {
        await useDeleteProtocol(protocol, comment);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('protocolDeleted')]} />, toastOptionsTopRight);
    };

    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const usePrint = async () => {
        setLoading(true);

        const pdfFile = await getHandoversPDFByFilter({
            filter: appliedFilter,
        });

        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            setLoading(false);
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            setLoading(false);
            return false;
        }
    };

    const useExportExcel = async () => {
        setExportLoading(true);

        try {
            const excelFile = await getHandoversExcelByFilter({
                filter: appliedFilter,
            });

            const url = URL.createObjectURL(excelFile);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'handover-protocols.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(<CustomToastContent content={[`${t('exportError')}`]} whiteText />, toastOptionsTopRight);
        } finally {
            setExportLoading(false);
        }
    };

    const isCountedProtocolItem = (item: HandoverProtocol['items'][number]) =>
        item.itemType === 'DEVICE' ||
        (item.itemType === 'OTHER' &&
            (serviceableMaterialPassportIds.has(item.itemId) || item.oilId != null));

    const getItemsDeviceCount = (items: HandoverProtocol['items']) => {
        if (!Array.isArray(items)) {
            return 0;
        }

        return items.filter(isCountedProtocolItem).reduce((acc, item) => acc + (item.itemCount || 0), 0);
    };

    const getProtocolDeviceCount = (protocol: HandoverProtocol) => getItemsDeviceCount(protocol.items);
    const getProtocolTypeLabel = (taskCategory?: string) => (taskCategory ? t(taskCategory) : '');
    const getDeviceModels = (items: HandoverProtocol['items']) => {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .filter((item) => isCountedProtocolItem(item) && item.itemName)
            .map((item) => (item.itemType === 'OTHER' ? 'REED' : item.itemName));
    };
    const renderDeviceModels = (items: HandoverProtocol['items']) => {
        const models = getDeviceModels(items);

        if (models.length === 0) {
            return '-';
        }

        return (
            <div className='d-flex flex-column align-items-center'>
                {models.map((model, index) => (
                    <span key={`${model}-${index}`}>{model}</span>
                ))}
            </div>
        );
    };
    const renderProtocolDate = (value: HandoverProtocol['date']) => (
        <div className='d-flex flex-column align-items-center'>
            <span>{dateFormat(value, 'dd.mm.yyyy')}</span>
            <span>{dateFormat(value, 'HH:MM:ss')}</span>
        </div>
    );
    const renderBranchName = (protocol: HandoverProtocol) => {
        if (!protocol.renamedBranchReplacement) {
            return protocol.branchName;
        }

        const previousDate = protocol.previousDeviceReturnAt
            ? dateFormat(protocol.previousDeviceReturnAt, 'dd.mm.yyyy')
            : '-';
        const matchingDistributor = protocol.previousProtocolDistributor || protocol.distributor || '-';
        const details = t('renamedBranchReplacementDetails', {
            branch: protocol.previousBranchName || protocol.previousBranchId || '-',
            distributor: matchingDistributor,
            date: previousDate,
        });

        return (
            <div className='d-flex flex-column align-items-center' title={details}>
                <span>{protocol.branchName}</span>
                <small className='fw-bold'>{t('possibleRenamedBranch')}</small>
                <small>{t('matchingBranchName', { branch: protocol.previousBranchName || '-' })}</small>
                <small>{t('matchingProtocolDistributor', { distributor: matchingDistributor })}</small>
            </div>
        );
    };
    const formatFilterDate = (value?: string) => {
        if (!value) {
            return t('all');
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : dateFormat(date, 'dd.mm.yyyy');
    };
    const getDistributorFilterLabel = () => {
        if (!appliedFilter.id) {
            return t('all');
        }

        return (
            items.find((item) => item.distributorId.toString() === appliedFilter.id)?.distributor || appliedFilter.id
        );
    };
    const getAppliedFilterCriteriaText = () =>
        [
            `${t('fromDate')}: ${formatFilterDate(appliedFilter.after)}`,
            `${t('toDate')}: ${formatFilterDate(appliedFilter.before)}`,
            `${t('taskType')}: ${appliedFilter.type ? getProtocolTypeLabel(appliedFilter.type) : t('all')}`,
            `${t('searchText')}: ${appliedFilter.search?.trim() || t('all')}`,
            `${t('distributor')}: ${getDistributorFilterLabel()}`,
        ].join(' | ');
    const getModelCount = (modelCounts: Record<string, number> | undefined, model: string) => modelCounts?.[model] || 0;
    const formatCount = (count: number) => count || '-';
    const getSummaryModels = (summary?: HandoversSummary) => {
        return Array.from(
            new Set([
                ...Object.keys(summary?.newDevicesByModel || {}),
                ...Object.keys(summary?.servicedDevicesByModel || {}),
                ...Object.keys(summary?.returnedDevicesByModel || {}),
            ])
        ).sort((firstModel, secondModel) => firstModel.localeCompare(secondModel));
    };
    const renderDeviceModelCountsSummary = (summary?: HandoversSummary) => {
        const models = getSummaryModels(summary);

        if (models.length === 0) {
            return null;
        }

        const rows = models.map((model) => {
            const newDevices = getModelCount(summary?.newDevicesByModel, model);
            const servicedDevices = getModelCount(summary?.servicedDevicesByModel, model);
            const returnedDevices = getModelCount(summary?.returnedDevicesByModel, model);

            return {
                model,
                newDevices,
                servicedDevices,
                returnedDevices,
                total: newDevices + servicedDevices + returnedDevices,
            };
        });
        const totals = rows.reduce(
            (acc, row) => ({
                newDevices: acc.newDevices + row.newDevices,
                servicedDevices: acc.servicedDevices + row.servicedDevices,
                returnedDevices: acc.returnedDevices + row.returnedDevices,
                total: acc.total + row.total,
            }),
            { newDevices: 0, servicedDevices: 0, returnedDevices: 0, total: 0 }
        );

        return (
            <div className='w-100 mt-3 mb-3'>
                <div className='border rounded overflow-hidden bg-white w-100'>
                    <div className='background-dark color-main d-flex justify-content-between align-items-center px-3 py-2'>
                        <div className='d-flex flex-column flex-lg-row align-items-lg-center gap-1'>
                            <span className='fw-bold'>{tableT('modelCountsSummary')}</span>
                            <span className='small'>{getAppliedFilterCriteriaText()}</span>
                        </div>
                        <span className='small'>
                            {tableT('total')}: {totals.total}
                        </span>
                    </div>
                    <BootstrapTable responsive size='sm' className='mb-0 text-center align-middle'>
                        <thead>
                            <tr>
                                <th className='text-start ps-3'>{tableT('models')}</th>
                                <th>{tableT('newDevices')}</th>
                                <th>{tableT('servicedDevices')}</th>
                                <th>{tableT('returnedDevices')}</th>
                                <th>{tableT('total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.model}>
                                    <td className='text-start ps-3'>{row.model}</td>
                                    <td>{formatCount(row.newDevices)}</td>
                                    <td>{formatCount(row.servicedDevices)}</td>
                                    <td>{formatCount(row.returnedDevices)}</td>
                                    <td className='fw-bold'>{formatCount(row.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className='fw-bold'>
                                <td className='text-start ps-3'>{tableT('total')}</td>
                                <td>{formatCount(totals.newDevices)}</td>
                                <td>{formatCount(totals.servicedDevices)}</td>
                                <td>{formatCount(totals.returnedDevices)}</td>
                                <td>{formatCount(totals.total)}</td>
                            </tr>
                        </tfoot>
                    </BootstrapTable>
                </div>
            </div>
        );
    };

    const protocolTableItems = items.map((item) => ({
        ...item,
        deviceCount: getProtocolDeviceCount(item),
    }));
    const hasReplacementPairs = protocolTableItems.some((item) => item.replacementPair);
    const hasRenamedBranchReplacements = protocolTableItems.some((item) => item.renamedBranchReplacement);
    const [handoversSummary, setHandoversSummary] = useState<HandoversSummary>();
    const [selectedProtocol, setSelectedProtocol] = useState<HandoverProtocol>();

    useEffect(() => {
        let isActive = true;

        if (!hasSearched) {
            setHandoversSummary(undefined);
            return;
        }

        const fetchSummary = async () => {
            try {
                const summary = await getHandoversSummary({
                    filter: appliedFilter,
                });

                if (isActive) {
                    setHandoversSummary(summary);
                }
            } catch {
                if (isActive) {
                    setHandoversSummary(undefined);
                }
            }
        };

        fetchSummary();

        return () => {
            isActive = false;
        };
    }, [appliedFilter, hasSearched, pager.totalCount]);

    if (!hasSearched) {
        return (
            <div className='d-flex flex-column justify-content-center align-items-center w-100 py-5'>
                <h5 className='text-center text-muted mb-0'>{t('selectSearchCriteria')}</h5>
            </div>
        );
    }

    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <h5 className='mb-2 d-flex justify-content-center w-100'>{t('handoverProtocols')}</h5>
                    <div className='d-flex gap-2 mb-2'>
                        <Button variant='warning' onClick={usePrint} disabled={loading}>
                            {loading ? <Spinner animation='border' size='sm' /> : t('print')}
                        </Button>
                        <Button variant='success' onClick={useExportExcel} disabled={exportLoading}>
                            {exportLoading ? <Spinner animation='border' size='sm' /> : t('exportExcel')}
                        </Button>
                    </div>
                    {(hasRenamedBranchReplacements || hasReplacementPairs) && (
                        <div className='d-flex flex-row flex-wrap justify-content-start align-items-start align-self-start w-100 gap-2 mb-2 text-start'>
                            {hasRenamedBranchReplacements && (
                                <div className='replacement-protocol-hint renamed-branch-replacement-protocol-hint'>
                                    <span className='replacement-protocol-marker renamed-branch-replacement-protocol-marker' />
                                    <span>{t('renamedBranchReplacementProtocolsHint')}</span>
                                </div>
                            )}
                            {hasReplacementPairs && (
                                <div className='replacement-protocol-hint'>
                                    <span className='replacement-protocol-marker' />
                                    <span>{t('replacementProtocolsHint')}</span>
                                </div>
                            )}
                        </div>
                    )}
                    <div className='w-100 mb-3'>
                        <CustomTable
                            columns={{
                                headings: handoverProtocolsShortTableHeaders,
                                items: protocolTableItems.map((protocol) => [
                                    protocol.clientName,
                                    renderBranchName(protocol),
                                    protocol.address,
                                    getProtocolTypeLabel(protocol.taskCategory),
                                    protocol.deviceCount ?? 0,
                                    renderDeviceModels(protocol.items),
                                    protocol.distributor,
                                    renderProtocolDate(protocol.date),
                                ]),
                            }}
                            actionHook={useDelete}
                            comment={comment}
                            setComment={setComment}
                            editComponent={AddEditProtocol}
                            editIcon={faPrint}
                            accessEditRoles={[Role.COORDINATOR]}
                            accessActionRoles={[Role.COORDINATOR]}
                            tableData={protocolTableItems}
                            refetchEntitiesHook={refetchEntitiesHook}
                            viewComponentObject={setSelectedProtocol}
                            rowClassName={(protocol) => {
                                if (protocol.renamedBranchReplacement) {
                                    return 'renamed-branch-replacement-protocol-row';
                                }

                                return protocol.replacementPair ? 'replacement-protocol-row' : '';
                            }}
                        />
                    </div>
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                    {renderDeviceModelCountsSummary(handoversSummary)}
                    {selectedProtocol && (
                        <Suspense fallback={<Spinner animation='border' />}>
                            <NewRequestProvider>
                                <ProtocolBranchInfoModal
                                    protocol={selectedProtocol}
                                    onClose={() => setSelectedProtocol(undefined)}
                                />
                            </NewRequestProvider>
                        </Suspense>
                    )}
                </>
            ) : (
                <NoDataComponent noDataText={t('handoverProtocols')} />
            )}
        </>
    );
};

const ProtocolBranchInfoModal = ({ protocol, onClose }: { protocol: HandoverProtocol; onClose: () => void }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { onBranchSelect, onClientSelect } = useContext(NewRequestContext);
    const [showObjectModal, setShowObjectModal] = useState(false);
    const [isLoadingBranch, setIsLoadingBranch] = useState(true);

    useEffect(() => {
        let isActive = true;

        const openBranchInfo = async () => {
            setIsLoadingBranch(true);

            try {
                const [client, branch] = await Promise.all([
                    getClientById(protocol.clientId.toString()),
                    getBranchByIdInRequestView(protocol.branchId),
                ]);

                if (!isActive) {
                    return;
                }

                onClientSelect(client);
                onBranchSelect(branch);
                localStorage.setItem('isModalOpen', JSON.stringify(true));
                localStorage.setItem('selectedClient', JSON.stringify(client));
                setShowObjectModal(true);
            } catch {
                toast.error(
                    <CustomToastContent content={[t('branchInfoLoadError')]} whiteText />,
                    toastOptionsTopRight
                );
                onClose();
            } finally {
                if (isActive) {
                    setIsLoadingBranch(false);
                }
            }
        };

        openBranchInfo();

        return () => {
            isActive = false;
        };
        // Context callbacks are intentionally excluded to avoid refetching after the modal context updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protocol.branchId, protocol.clientId]);

    const setModalVisibility: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        setShowObjectModal((previousValue) => {
            const nextValue = typeof value === 'function' ? value(previousValue) : value;

            if (!nextValue) {
                onClose();
            }

            return nextValue;
        });
    };

    return (
        <>
            {isLoadingBranch && (
                <div className='d-flex justify-content-center my-2'>
                    <Spinner animation='border' />
                </div>
            )}
            {showObjectModal && <NewRequest showModal={showObjectModal} setShowModal={setModalVisibility} />}
        </>
    );
};
